import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomModal, CustomModalProps, StyledTextInput } from 'components/shared';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks';
import { OrderDetailsDTO } from 'interfaces';
import { HTTPService } from 'service';
import { errorHandler } from 'utils/errorHandler';

type EditInternalOrderIdModalProps = Pick<CustomModalProps, 'onClose'> & {
   orderId: string;
   internalOrderId: string | null;
   onEditInternalOrderIdSuccess: (updatedOrderDetailsDTO: OrderDetailsDTO) => void;
};

const MODAL_WIDTH = 550;

const EditInternalOrderIdModal = ({
   orderId,
   internalOrderId,
   onEditInternalOrderIdSuccess,
   onClose,
}: EditInternalOrderIdModalProps) => {
   const { t } = useTranslation();
   const [enteredInternalOrderId, setEnteredInternalOrderId] = useState(internalOrderId ?? '');

   const {
      isLoading,
      errorInfo,
      setIsLoading,
      setErrorInfo,
      showCloseWarning,
      handleShowCloseWarning,
      handleHideCloseWarning,
      closeModalHandler,
   } = useModal({
      onClose,
   });

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleEditInternalOrderIdError = (error: unknown) =>
      errorHandler(error, () => setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN));

   const handleEditInternalOrderId = () => {
      setIsLoading(true);
      HTTPService.editInternalOrderId(orderId, {
         internalOrderId: enteredInternalOrderId ? enteredInternalOrderId : undefined,
      })
         .then(editInternalOrderIdResponse => {
            closeModalHandler();
            onEditInternalOrderIdSuccess(editInternalOrderIdResponse.data);
         })
         .catch(handleEditInternalOrderIdError)
         .finally(() => setIsLoading(false));
   };

   return (
      <CustomModal
         opened
         onClose={handleShowCloseWarning}
         title={t('edit_internal_order_id_modal.title')}
         primaryButtonProps={{
            text: t('edit_internal_order_id_modal.confirm'),
            onClick: handleEditInternalOrderId,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'text',
            onClick: handleShowCloseWarning,
         }}
         closeWarningModalProps={{
            opened: showCloseWarning,
            onClose: handleHideCloseWarning,
            onConfirm: closeModalHandler,
         }}
         isLoading={isLoading}
         error={errorInfo}
         size={MODAL_WIDTH}
      >
         <StyledTextInput
            type="text"
            value={enteredInternalOrderId}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
               setEnteredInternalOrderId(event.currentTarget.value)
            }
            fullWidth
         />
      </CustomModal>
   );
};

export default EditInternalOrderIdModal;
